import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useLocation }                   from 'react-router-dom';
import { useApolloClient, useMutation }              from '@apollo/react-hooks';

import { CorvalMutation, CorvalMutationRemapProjectArgs, CorvalMutationToggleAllSubObjectivesArgs, CorvalMutationUpdateProjectCorvalManagedArgs, CorvalProject, CorvalProjectTemplate, CorvalProjectVersion, Uuid } from '../types';
import { RouterUtility, runIfFunction }                                                                                                                                                                             from '../util';
import { RenameProjectDialog }                                                                                                                                                               from '../views/pages/projects/operations/RenameProjectDialog';
import { DuplicateProjectDialog }                                                                                                                                                            from '../views/pages/projects/operations/DuplicateProjectDialog';
import { QueryDialogProps }                                                                                                                                                                  from '../views/components/from-query/QueryOpDialog';
import { usePrefStates }                                                                                                                                                                     from './usePrefStates';
import { useSystemModals, useSystemToasts }                                                                                                                                                  from './useSystem';
import { ProjectQueries }                                                                                                                                                                    from '../queries/ProjectQueries';
import { CompareProjectDialog }                                                                                                                                                              from '../views/pages/projects/operations/CompareProjectDialog';
import { ProjectService }                                                                                                                                                                    from "@src/services";
import { CorvalAssistantContextDecisionPointTypes, CorvalAssistantContextUpdateDecisionPointDialog }                                                                                         from "@src/views/pages/projects/CorvalAssistantContextUpdateDecisionPointDialog";
import { DecisionEngineQueries }                                                                                                                                                             from "@src/queries";
import { ProjectContext }                                                                                                                                                                    from "@src/views/pages/projects/ProjectIndexPage";
import { CreateProjectVersionDialog }                                                                                                                                                        from "@src/views/pages/projects/operations/CreateProjectVersionDialog";
import { ManageProjectVersionsDialog }                                                                                                                                                       from "@src/views/pages/projects/operations/ManageProjectVersionsDialog";
import { ProjectTemplateManagementDialog }                                                                                                                                                   from "@src/views/pages/projects/operations/ProjectTemplateManagementDialog";
import { CreateProjectDialog }                                                                                                                                                               from "@src/views/pages/projects/operations/CreateProjectDialog";
import { ProjectTemplatesManagementDialog }                                                                                                                                                  from "@src/views/pages/projects/operations/ProjectTemplatesManagementDialog";


interface UseProjectHook {

    /* Navigates the user to a project, preferrably using the last section / subsection */
    openProject( projectId: Uuid ): void,

    /* triggers the rename project dialog */
    renameProject( projectId?: Uuid, projectName?: string, onComplete?: QueryDialogProps['onComplete'] ): void,

    /* triggers the duplicate project dialog */
    duplicateProject( projectId?: Uuid, projectName?: string, onComplete?: QueryDialogProps['onComplete'] ): void,

    /* triggers a project comparison dialog */
    compareProjects( projectIds: Uuid[], title?: string, primaryProjectId?: Uuid ): void,

    /* triggers a project content maximization process */
    loadAllProjectContent( projectId: Uuid, onComplete?: ( data: CorvalMutation['toggleAllSubObjectives'] ) => void ): Promise<Error | CorvalMutation['toggleAllSubObjectives']>,

    /* triggers a project content minimization process */
    unloadAllProjectContent( projectId: Uuid, onComplete?: ( data: CorvalMutation['toggleAllSubObjectives'] ) => void ): Promise<Error | CorvalMutation['toggleAllSubObjectives']>,

    /* triggers a project remap update */
    remapProjectAdhoc( projectId?: Uuid, onRemapComplete?: ( data: CorvalMutation['remapProject'] ) => void ): Promise<Error | CorvalMutation['remapProject']>,

    /* triggers the context schema management dialog  */
    showProjectManagementSchemeUpdateDialog( isCorvalManaged: boolean, onComplete?: ( project: CorvalProject ) => void ): void,

    /* triggers a project management scheme update */
    updateProjectManagementScheme( projectId?: Uuid, isCorvalManaged?: boolean, onContextChangeComplete?: ( data: CorvalMutation['updateProjectCorvalManaged'] ) => void ): Promise<Error | CorvalMutation['updateProjectCorvalManaged']>,

    /* Returns a planning page route for the given project */
    getPlanningPageRoute( projectId: Uuid ): string,

    /* send user to a subsection within a section */
    redirectToSubSectionWithin( routePathKey: string ): void,

    /* Updates the subpath section user should return to within a section */
    updateLastSubSection( routePathKey: string, routePath: string ): void,

    /* Updates the root section user should return to, and it's subpath */
    updateRootAndSubSection( routeBasePath: string, routeSubsectionPath: string ): void,

    /* Clears the root section user should return to */
    clearRootSection(): void,

    /* Gets a project version object by id, or returns the default version if not found;
     Requires a project version array to be present. Uses the current preference state for referring
     to the last known project version id for a given project a user was looking at. */
    getMaybeCurrentProjectVersionFromSet( projectVersions: any[] ): Partial<CorvalProjectVersion> | null;

    /* triggers a project version creation dialog */
    createNewProjectVersion( projectId: Uuid, projectVersionId: Uuid, onComplete?: ( newProjectVersionId: Uuid ) => void ): void,

    /* triggers a project version management tool dialog */
    showProjectVersionManagementDialog( projectId: Uuid ): void,

    /* triggers a project template creation dialog */
    showProjectTemplateCreationDialog( projectId: Uuid, projectVersionId: Uuid, onComplete?: () => void ): void,

    /* triggers all project template edit dialog */
    showProjectTemplatesManagementDialog( onComplete?: ( templateListing: CorvalProjectTemplate[] ) => void ): void,

    /* triggers a project template edit dialog */
    showProjectTemplateEditDialog( templateId: Uuid, onComplete?: () => void ): void,

    /* triggers a new project from template dialog */
    showCreateProjectFromTemplateDialog( onComplete?: () => void ): void,
}

export const useProject = ( projectId: Uuid = null ): UseProjectHook => {

    const history                         = useHistory(),
          location                        = useLocation(),
          toastActions                    = useSystemToasts(),
          modalActions                    = useSystemModals(),
          [ prefState, prefs, prefEnums ] = usePrefStates(),
          [ , projectSpecificPrefs ]      = usePrefStates( projectId ),
          apolloClient                    = useApolloClient(),
          { setProjectVersion }           = useContext( ProjectContext );

    // Mutations
    const [ remapProject, remapProjectReq ]                       = useMutation<{ remapProject: CorvalMutation['remapProject'] }, CorvalMutationRemapProjectArgs>( ProjectQueries.mutations.REMAP_PROJECT( `id updatedUtc` ) ),
          [ setProjectContext, setProjectContextReq ]             = useMutation<{
              updateProjectCorvalManaged: CorvalMutation['updateProjectCorvalManaged']
          }, CorvalMutationUpdateProjectCorvalManagedArgs>( ProjectQueries.mutations.UPDATE_PROJECT_CONTEXT( ProjectQueries.fragments.PROJECTS.CONFIG_FIELDS ) ),
          [ setAllContentForProject, setAllContentForProjectReq ] = useMutation<{ toggleAllSubObjectives: CorvalMutation['toggleAllSubObjectives'] }, CorvalMutationToggleAllSubObjectivesArgs>( DecisionEngineQueries.mutations.TOGGLE_ALL_PROJECT_OBJECTIVES() );

    useEffect( () => {}, [ prefState ] );

    const openProject: UseProjectHook['openProject'] = ( projectId ) => {

        let preferredRoute = prefs.get( prefEnums.PROJECT_SECTION ),
            defaultRoute   = 'PROJECT.PLANNING';

        try{

            // go to preferred, last known section of project
            history.push( RouterUtility.getRoute( preferredRoute, { projectId } ) );

        } catch( e ) {

            console.warn( `Project route "${ preferredRoute }" does not exist.` );

            // go to default route
            history.push( RouterUtility.getRoute( defaultRoute, { projectId } ) );
        }
    };

    const renameProject: UseProjectHook['renameProject'] = ( projectId, projectName, onComplete = null ) => {

        modalActions.show(
            <RenameProjectDialog
                projectId={ projectId }
                projectName={ projectName }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Project Renamed Successfully' }
            />, {
                size: 'tiny'
            } );
    };

    const duplicateProject: UseProjectHook['duplicateProject'] = ( projectId, projectName, onComplete = null ) => {

        modalActions.show(
            <DuplicateProjectDialog
                projectId={ projectId }
                projectName={ projectName }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Project Duplicated Successfully' }
            />, {
                size: 'tiny'
            } );
    };

    const compareProjects: UseProjectHook['compareProjects'] = ( projectNames, title = 'Compare Projects', primaryProjectId = null ) => {

        modalActions.show(
            <CompareProjectDialog
                title={ title }
                projectIds={ projectNames }
                primaryProjectId={ primaryProjectId }
            />, {
                size:  'fullscreen',
                style: { maxWidth: `80vw` }
            }
        );
    };

    const loadAllProjectContent: UseProjectHook['loadAllProjectContent'] = async ( projectId, onComplete = null ) => {

        return toggleAllProjectContent( projectId, true, onComplete );
    };

    const unloadAllProjectContent: UseProjectHook['loadAllProjectContent'] = async ( projectId, onComplete = null ) => {

        return toggleAllProjectContent( projectId, false, onComplete );
    };

    const toggleAllProjectContent = async ( projectId: Uuid, enabledValue = true, onComplete = null ) => {

        toastActions.info(
            'Setting Project Content...',
            `${ enabledValue ? 'Loading' : 'Removing' } all available objectives and activities.`,
            {
                dismissable: false,
                timeoutMS:   0,
                icon:        { name: 'cog', loading: true },
                id:          `max-content-project-request-toast`
            } );

        try{

            // Set all inputs for the project to the enabled value
            return setAllContentForProject( {
                variables: {
                    input: {
                        projectId,
                        toggleState: enabledValue,
                    }
                }
            } ).then( async ( response ) => {

                if( response ){

                    toastActions.removeById( `max-content-project-request-toast` );

                    toastActions.success( `All Project Content ${ enabledValue ? 'Loaded' : 'Unloaded' }!` );

                    await remapProjectAdhoc( projectId, () => { window.location.reload(); } );

                    runIfFunction( onComplete, response.data.toggleAllSubObjectives );

                    return response.data.toggleAllSubObjectives;

                } else {

                    toastActions.error( 'Mutation Error', remapProjectReq.error.message, { timeoutMS: 0 } );

                    return Promise.reject( remapProjectReq.error.message );
                }

            } );

        } catch( e ) {

            toastActions.error( 'Network Error', `${ e.message }` );

            return Promise.reject( e );
        }
    };

    const remapProjectAdhoc: UseProjectHook['remapProjectAdhoc'] = ( projectId, onRemapComplete = null ) => {

        toastActions.info(
            'Remapping Project...',
            'Running the remapping algorithm in the background, please wait until this operation is complete.',
            {
                dismissable: false,
                timeoutMS:   0,
                icon:        { name: 'cog', loading: true },
                id:          `remap-project-request-toast`
            } );

        try{

            return remapProject( {
                variables: {
                    input: { projectId }
                }
            } ).then( ( response ) => {

                if( response ){

                    toastActions.removeById( `remap-project-request-toast` );

                    toastActions.success( 'Project Remapped!' );

                    runIfFunction( onRemapComplete, response.data.remapProject );

                    return response.data.remapProject;

                } else {

                    toastActions.error( 'Mutation Error', remapProjectReq.error.message, { timeoutMS: 0 } );

                    return Promise.reject( remapProjectReq.error.message );
                }

            } );

        } catch( e ) {

            toastActions.error( 'Network Error', `${ e.message }` );

            return Promise.reject( e );
        }
    };

    const getPlanningPageRoute: UseProjectHook['getPlanningPageRoute'] = ( projectId ) => {

        return RouterUtility.placeParamsInPath( `${ RouterUtility.getRoutePath( 'PROJECT.PLANNING' ) }`, { projectId } );

    };

    const showProjectManagementSchemeUpdateDialog = ( isCorvalManaged: boolean = true, onComplete: ( project: CorvalProject ) => void = null ) => {

        modalActions.show(
            <CorvalAssistantContextUpdateDecisionPointDialog
                isCorvalManaged={ isCorvalManaged }
                innerContainerOnly={ true }
                isOpen={ true }
                dialogProps={ {
                    onComplete: data => handleContextGuidanceModalComplete( data, onComplete ),
                    onClose:    handleContextGuidanceModalClosed,
                } }
            />
        );
    };

    const handleContextGuidanceModalComplete = async ( data: { selectedType: CorvalAssistantContextDecisionPointTypes }, onContextChangeComplete: ( project: CorvalProject ) => void = null ) => {

        const { selectedType } = data;

        if( selectedType as CorvalAssistantContextDecisionPointTypes ){

            switch( selectedType ){

                case CorvalAssistantContextDecisionPointTypes.MANAGED:

                    await updateProjectManagementScheme( projectId, true, onContextChangeComplete );

                    break;

                case CorvalAssistantContextDecisionPointTypes.UNMANAGED:

                    await updateProjectManagementScheme( projectId, false, onContextChangeComplete );

                    break;
            }
        }

        modalActions.toggle( false );
    };

    const handleContextGuidanceModalClosed = () => {

        modalActions.toggle( false );
    };

    const updateProjectManagementScheme: UseProjectHook['updateProjectManagementScheme'] = ( projectId, isCorvalManaged, onContextChangeComplete = null ) => {

        toastActions.info(
            'Updating Project...',
            'Setting how Corval Assistant works with your data.',
            {
                dismissable: false,
                timeoutMS:   0,
                icon:        { name: 'cog', loading: true },
                id:          `update-project-context-request-toast`
            } );

        try{

            return setProjectContext( {
                variables:      {
                    input: {
                        projectId,
                        isCorvalManaged
                    }
                },
                refetchQueries: [
                    {
                        // Update the config for top level contexts
                        query:     ProjectQueries.queries.GET_PROJECT( ProjectQueries.fragments.PROJECTS.CONFIG_FIELDS ),
                        variables: { id: projectId },
                    }
                ]
            } ).then( ( response ) => {

                toastActions.removeById( `update-project-context-request-toast` );

                if( response ){

                    toastActions.success( 'Project Context Updated!' );

                    // Reset the version id to null or the default version
                    const nextProjectVersionId = ( isCorvalManaged ) ? null : ProjectService.getDefaultProjectVersion( response?.data?.updateProjectCorvalManaged?.projectVersions ?? [] )?.id ?? null;

                    runIfFunction( setProjectVersion, nextProjectVersionId );

                    runIfFunction( onContextChangeComplete, response.data.updateProjectCorvalManaged );

                    return response.data.updateProjectCorvalManaged;

                } else {

                    toastActions.error( 'Mutation Error', remapProjectReq.error.message, { timeoutMS: 0 } );

                    return Promise.reject( remapProjectReq.error.message );
                }

            } );

        } catch( e ) {

            toastActions.removeById( `update-project-context-request-toast` );

            toastActions.error( 'Network Error', `${ e.message }` );

            return Promise.reject( e );
        }
    };

    /* This method helps redirect users to the last subsection of an area they were at */
    const redirectToSubSectionWithin: UseProjectHook['redirectToSubSectionWithin'] = ( routePathKey ) => {

        if( !projectId ) return null;

        let subSections    = prefs.get( prefEnums.PROJECT_SUBSECTION ),
            preferredRoute = subSections[ routePathKey ] ?? null;

        if( !preferredRoute ) return null;

        let preferredRoutePath = RouterUtility.getRoute( preferredRoute, { projectId } ),
            isSameSubPath      = preferredRoutePath.includes( location.pathname ),
            isNotSameRoute     = preferredRoutePath !== location.pathname;

        try{

            // go to preferred, last known section of project area
            if( preferredRoute && isSameSubPath && isNotSameRoute ){

                console.log( { preferredRoute } );

                history.push( RouterUtility.getRoute( preferredRoute, { projectId } ) );
            }

        } catch( e ) {

            console.warn( `Project route "${ preferredRoute }" does not exist.` );
        }

    };

    const updateLastSubSection: UseProjectHook['updateLastSubSection'] = useCallback( ( routePathKey, routePath ) => {

        prefs.update( prefEnums.PROJECT_SUBSECTION, {

            ...prefs.get( prefEnums.PROJECT_SUBSECTION ),

            [ routePathKey ]: routePath
        } );

    }, [ prefState ] );

    const updateRootAndSubSection: UseProjectHook['updateRootAndSubSection'] = useCallback( ( routeBasePath, routeSubsectionPath ) => {

        prefs.update( null, {
            [ prefEnums.PROJECT_SECTION ]:    routeBasePath,
            [ prefEnums.PROJECT_SUBSECTION ]: {
                ...prefs.get( prefEnums.PROJECT_SUBSECTION ),
                [ routeBasePath ]: routeSubsectionPath,
            }
        } );

    }, [ prefState ] );

    const clearRootSection: UseProjectHook['clearRootSection'] = useCallback( () => {

        prefs.update( null, {
            [ prefEnums.PROJECT_SECTION ]: null,
        } );

    }, [ prefState ] );

    const getMaybeCurrentProjectVersionFromSet: UseProjectHook['getMaybeCurrentProjectVersionFromSet'] = ( projectVersions ) => {

        const projectVersionId = projectSpecificPrefs.get( prefEnums.PROJECT_VERSION_ID );

        return ProjectService.getProjectVersionById( projectVersions, projectVersionId, true );
    };

    const createNewProjectVersion: UseProjectHook['createNewProjectVersion'] = ( projectId, projectVersionId, onComplete ) => {

        /* Launch project version creation modal */
        modalActions.show( (
            <CreateProjectVersionDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                onVersionCreated={ onComplete }
                onClose={ () => modalActions.toggle( false ) }
                successToastLabel={ 'New Project Version Created' }

            />
        ), {
            size:     'tiny',
            centered: false,
        } );
    };

    const showProjectVersionManagementDialog: UseProjectHook['showProjectVersionManagementDialog'] = ( projectId ) => {

        /* Launch project version management modal */
        modalActions.show( (
            <ManageProjectVersionsDialog
                projectId={ projectId }
                title={ 'Manage Project Versions' }
            />
        ), {
            size:     'tiny',
            centered: false,
        } );
    };

    const showProjectTemplateCreationDialog: UseProjectHook['showProjectTemplateCreationDialog'] = ( projectId, projectVersionId, onComplete ) => {

        modalActions.show( (
            <ProjectTemplateManagementDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                onComplete={ onComplete }
            />
        ), {
            size:     'small',
            centered: true,
        } );
    };

    const showProjectTemplatesManagementDialog: UseProjectHook['showProjectTemplatesManagementDialog'] = ( onComplete ) => {

        modalActions.show( (
            <ProjectTemplatesManagementDialog onComplete={ onComplete }/>
        ), {
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false
        } );
    };

    const showProjectTemplateEditDialog: UseProjectHook['showProjectTemplateEditDialog'] = ( templateId, onComplete ) => {

        modalActions.show( (
            <ProjectTemplateManagementDialog
                templateId={ templateId }
                onComplete={ onComplete }
            />
        ), {
            size:     'small',
            centered: true,
        } );
    };

    const showCreateProjectFromTemplateDialog: UseProjectHook['showCreateProjectFromTemplateDialog'] = ( onComplete ) => {

        modalActions.show( (
            <CreateProjectDialog
                onComplete={ onComplete }
            />
        ), {
            size:                 'large',
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false
        } );
    };

    return {
        openProject,
        renameProject,
        duplicateProject,
        compareProjects,
        loadAllProjectContent,
        unloadAllProjectContent,
        remapProjectAdhoc,
        getPlanningPageRoute,
        showProjectManagementSchemeUpdateDialog,
        updateProjectManagementScheme,
        redirectToSubSectionWithin,
        updateLastSubSection,
        updateRootAndSubSection,
        clearRootSection,
        getMaybeCurrentProjectVersionFromSet,
        createNewProjectVersion,
        showProjectVersionManagementDialog,
        showProjectTemplateCreationDialog,
        showProjectTemplatesManagementDialog,
        showProjectTemplateEditDialog,
        showCreateProjectFromTemplateDialog,
    };
};
